import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { nowIsoWithOffset } from '../utils/datetime'

// ── Token helper ──────────────────────────────────────────────────────────────

/** Generates a cryptographically random hex string of the given byte length. */
export function generateHex(bytes = 16): string {
  const arr = new Uint8Array(bytes)
  crypto.getRandomValues(arr)
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')
}

// ── Types ─────────────────────────────────────────────────────────────────────

/** Role assigned by the organizer to an accepted participant. */
export type ParticipantRole = 'Audience' | 'Speaker' | 'Staff' | 'Host'

/** Presentation request/review lifecycle status. */
export type PresentationStatus = 'none' | 'reminder_sent' | 'uploaded' | 'accepted' | 'rejected'

/** Roles eligible for presentation management. */
export const PRESENTATION_ELIGIBLE_ROLES: ParticipantRole[] = ['Speaker', 'Host']

/** Metadata about a participant's presentation file request and upload. */
export interface PresentationMeta {
  status: PresentationStatus
  /** ISO datetime of the most recent reminder sent. */
  lastReminderAt?: string
  /** ISO datetime when the file was uploaded. */
  uploadedAt?: string
  fileName?: string
  fileType?: string
  fileSize?: number
  /** Optional rejection message from the organizer. */
  rejectionMessage?: string
  /** True when the organizer uploaded the file directly (backup path). */
  uploadedByOrganizer?: boolean
}

/** A single participant registration record. */
export interface Registration {
  id: string
  eventId: string
  /**
   * Random hex string generated on the frontend.
   * Mocks the RSA-signed secretToken that will come from the backend.
   * Used to identify the registration via the `?tk=` URL param.
   */
  secretToken: string
  firstName: string
  lastName?: string
  email: string
  company: string
  consentPersonalData: boolean
  consentMedia: boolean
  /**
   * `'pending'`  — awaiting organizer review.
   * `'accepted'` — organizer accepted, or auto-accepted (was invited / backup path).
   * `'rejected'` — organizer rejected.
   */
  status: 'pending' | 'accepted' | 'rejected'
  /** ISO datetime string with UTC offset, recorded at submission. */
  registeredAt: string
  /** true when the organizer filled the form on behalf of the participant. */
  registeredByOrganizer: boolean
  /** Optional message included in a rejection email. */
  rejectionMessage?: string
  /**
   * Role assigned by the organizer. Only meaningful when status is 'accepted'.
   * Defaults to 'Audience' upon acceptance.
   */
  role?: ParticipantRole
  /**
   * Presentation request/review state. Only applicable when role is Speaker or Host.
   * Undefined is treated as { status: 'none' }.
   */
  presentation?: PresentationMeta
  /**
   * ISO datetime of the most recent participation reminder sent by the organizer.
   * Undefined means no reminder has been sent yet.
   */
  lastParticipationReminderAt?: string
  /**
   * Whether this participant physically attended the event.
   * Set to true when their QR code is scanned (or manually confirmed) at the venue.
   */
  attended?: boolean
  /**
   * ISO datetime when attendance was confirmed.
   * Mocks the backend-generated timestamp that a real implementation would receive
   * from the server after the staff device calls POST /attendance.
   */
  attendedAt?: string
  /**
   * A private, participant-specific note written by the organizer.
   * Shown only on this participant's ticket page. Useful for seating assignments,
   * team groupings, or any personal instructions. Backend-persisted (`organizerNote` on
   * the API participant); this field is a read-only display projection populated from
   * the API response (PATH A) or the legacy Redux fixture (PATH B) — writes go through
   * `useSetParticipantNoteMutation`, not a local reducer.
   */
  privateNote?: string
  /**
   * ISO datetime of the most recent Certificate of Attendance send (or re-send).
   * Undefined means no CoA has been sent yet. Overwritten (not appended) on re-send,
   * per EP-13. Cleared when attendance is manually unmarked, since the participant no
   * longer qualifies for a certificate.
   */
  certificateLastSentAt?: string
}

interface RegistrationsState {
  list: Registration[]
}

const initialState: RegistrationsState = { list: [] }

// ── Slice ─────────────────────────────────────────────────────────────────────

const registrationsSlice = createSlice({
  name: 'registrations',
  initialState,
  reducers: {
    /** Add a single new registration. */
    addRegistration(state, action: PayloadAction<Registration>) {
      state.list.push(action.payload)
    },

    /** Permanently delete registrations by id. */
    deleteRegistrations(state, action: PayloadAction<string[]>) {
      const idSet = new Set(action.payload)
      state.list = state.list.filter((r) => !idSet.has(r.id))
    },

    /**
     * Accept or reject a set of registrations.
     * For rejections, an optional rejectionMessage is stored on each record.
     * Newly accepted registrations receive the default role 'Audience'.
     */
    updateRegistrationStatus(
      state,
      action: PayloadAction<{
        ids: string[]
        status: 'accepted' | 'rejected'
        rejectionMessage?: string
      }>
    ) {
      const { ids, status, rejectionMessage } = action.payload
      const idSet = new Set(ids)
      for (const reg of state.list) {
        if (idSet.has(reg.id)) {
          reg.status = status
          if (status === 'accepted') {
            reg.role = reg.role ?? 'Audience'
          }
          if (status === 'rejected' && rejectionMessage !== undefined) {
            reg.rejectionMessage = rejectionMessage
          }
        }
      }
    },

    /** Update the role of a single accepted participant. */
    updateRegistrationRole(state, action: PayloadAction<{ id: string; role: ParticipantRole }>) {
      const { id, role } = action.payload
      const reg = state.list.find((r) => r.id === id)
      if (reg && reg.status === 'accepted') {
        reg.role = role
      }
    },

    /**
     * Send (or re-send) a presentation request to one or more participants.
     * Only applies to Speaker/Host with status not 'uploaded' or 'accepted'.
     */
    sendPresentationReminder(state, action: PayloadAction<{ ids: string[] }>) {
      const idSet = new Set(action.payload.ids)
      const now = nowIsoWithOffset()
      for (const reg of state.list) {
        if (!idSet.has(reg.id)) continue
        if (!PRESENTATION_ELIGIBLE_ROLES.includes(reg.role ?? 'Audience')) continue
        const s = reg.presentation?.status ?? 'none'
        if (s === 'uploaded' || s === 'accepted') continue
        reg.presentation = {
          ...(reg.presentation ?? { status: 'none' }),
          status: 'reminder_sent',
          lastReminderAt: now,
        }
      }
    },

    /**
     * Record a presentation file upload (either participant or organizer backup).
     * The organizer backup path sets status directly to 'accepted'.
     */
    setPresentationUpload(
      state,
      action: PayloadAction<{
        id: string
        fileName: string
        fileType: string
        fileSize: number
        status: 'uploaded' | 'accepted'
        uploadedByOrganizer?: boolean
      }>
    ) {
      const { id, fileName, fileType, fileSize, status, uploadedByOrganizer } = action.payload
      const reg = state.list.find((r) => r.id === id)
      if (!reg) return
      reg.presentation = {
        ...(reg.presentation ?? { status: 'none' }),
        status,
        uploadedAt: nowIsoWithOffset(),
        fileName,
        fileType,
        fileSize,
        uploadedByOrganizer: uploadedByOrganizer ?? false,
        rejectionMessage: undefined,
      }
    },

    /**
     * Withdraw a pending (`uploaded`) presentation submission, reverting the status back to
     * `reminder_sent` so the participant can upload a new file. Mirrors the backend's
     * `deleteParticipantPresentation` semantics (only applies to `uploaded`, i.e. not yet
     * reviewed by the organizer).
     */
    deletePresentationUpload(state, action: PayloadAction<{ id: string }>) {
      const reg = state.list.find((r) => r.id === action.payload.id)
      if (!reg || reg.presentation?.status !== 'uploaded') return
      reg.presentation = {
        status: 'reminder_sent',
        lastReminderAt: reg.presentation.lastReminderAt,
      }
    },

    /**
     * Record that a participation reminder was sent to one or more accepted participants.
     * Applies to all accepted participants regardless of role.
     *
     * Note: per-participant custom message text is collected in the UI for preview
     * purposes only and is not persisted here — consistent with the presentation-ask
     * message that is also discarded after dispatch.
     */
    sendParticipationReminder(state, action: PayloadAction<{ ids: string[]; now: string }>) {
      const { ids, now } = action.payload
      const idSet = new Set(ids)
      for (const reg of state.list) {
        if (!idSet.has(reg.id)) continue
        if (reg.status !== 'accepted') continue
        reg.lastParticipationReminderAt = now
      }
    },

    /**
     * Record that a participant physically attended the event (QR scan or manual confirm).
     * Only applies to accepted registrations. Idempotent — the first attendedAt timestamp
     * is kept so re-scans do not overwrite the original check-in time.
     *
     * NOTE: in a real implementation the `now` timestamp would come from the backend
     * response after POST /attendance; here we use the client clock as a simulation.
     */
    markAttendance(state, action: PayloadAction<{ ids: string[]; now: string }>) {
      const { ids, now } = action.payload
      const idSet = new Set(ids)
      for (const reg of state.list) {
        if (!idSet.has(reg.id)) continue
        if (reg.status !== 'accepted') continue
        reg.attended = true
        if (!reg.attendedAt) reg.attendedAt = now
      }
    },

    /**
     * Manually clear the attendance record for a participant (post-event correction).
     * Only applies to accepted registrations. Clears both `attended` and `attendedAt`
     * so the participant moves back to the Non-Attendance list.
     *
     * NOTE: in a real implementation this would call a backend API to reverse the
     * attendance record; here it is simulated client-side.
     */
    unmarkAttendance(state, action: PayloadAction<{ ids: string[] }>) {
      const idSet = new Set(action.payload.ids)
      for (const reg of state.list) {
        if (!idSet.has(reg.id)) continue
        if (reg.status !== 'accepted') continue
        reg.attended = false
        reg.attendedAt = undefined
        reg.certificateLastSentAt = undefined
      }
    },

    /**
     * Accept or reject a set of uploaded presentations.
     * Only applies to registrations with presentation status 'uploaded'.
     */
    reviewPresentation(
      state,
      action: PayloadAction<{
        ids: string[]
        decision: 'accepted' | 'rejected'
        rejectionMessage?: string
      }>
    ) {
      const { ids, decision, rejectionMessage } = action.payload
      const idSet = new Set(ids)
      for (const reg of state.list) {
        if (!idSet.has(reg.id)) continue
        if (reg.presentation?.status !== 'uploaded') continue
        reg.presentation = {
          ...reg.presentation,
          status: decision,
          rejectionMessage: decision === 'rejected' ? (rejectionMessage ?? '') : undefined,
        }
      }
    },
  },
})

export const {
  addRegistration,
  deleteRegistrations,
  updateRegistrationStatus,
  updateRegistrationRole,
  sendPresentationReminder,
  setPresentationUpload,
  deletePresentationUpload,
  reviewPresentation,
  sendParticipationReminder,
  markAttendance,
  unmarkAttendance,
} = registrationsSlice.actions
export default registrationsSlice.reducer
